import React, { useState, useEffect } from 'react';
import './RandomnessTest.css';
import apiService from '../services/api';

interface TestResult {
  test_name: string;
  passed: boolean;
  p_value?: number;
  sample_size: number;
  timestamp: string;
}

interface QualityReport {
  status: string;
  sample_size: number;
  tests_run: number;
  tests_passed: number;
  pass_rate: number;
  recommendation: string;
  results?: TestResult[];
  message?: string;
  minimum_required?: number;
}

interface TestHistory {
  id: string;
  timestamp: string;
  testType: 'dummy' | 'real';
  entryCount: number;
  sampleSize: number;
  passRate: number;
  testsPassed: number;
  testsRun: number;
  recommendation: string;
  results: TestResult[];
}

const RandomnessTest: React.FC = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentReport, setCurrentReport] = useState<QualityReport | null>(null);
  const [testHistory, setTestHistory] = useState<TestHistory[]>([]);

  // Load test history from localStorage on component mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('randomness-test-history');
    if (savedHistory) {
      try {
        setTestHistory(JSON.parse(savedHistory));
      } catch (err) {
        // console.error('Failed to load test history:', err);
      }
    }
  }, []);

  // Save test history to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('randomness-test-history', JSON.stringify(testHistory));
  }, [testHistory]);

  const runDummyTest = async (entryCount: number) => {
    setIsRunning(true);
    setError(null);
    setCurrentReport(null);

    try {
      // Collect a large sample for testing
      await apiService.collectRandomnessSample(entryCount * 8);

      // Wait a moment for randomness data to be processed
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Get quality report
      const report = await apiService.getRandomnessQualityReport(1);
      setCurrentReport(report);

      // Add to test history
      const newTest: TestHistory = {
        id: `test_${Date.now()}`,
        timestamp: new Date().toISOString(),
        testType: 'dummy',
        entryCount,
        sampleSize: report.sample_size || 0,
        passRate: report.pass_rate || 0,
        testsPassed: report.tests_passed || 0,
        testsRun: report.tests_run || 0,
        recommendation: report.recommendation || 'No recommendation',
        results: report.results || []
      };

      setTestHistory(prev => [newTest, ...prev]);

    } catch (err) {
      setError(`Test failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsRunning(false);
    }
  };

  const runRealDrawTest = async (entryCount: number) => {
    setIsRunning(true);
    setError(null);
    setCurrentReport(null);

    try {
      // For real draws, we'll simulate by collecting a smaller sample
      // and then running multiple tests to build up data
      const sampleSize = Math.min(entryCount * 4, 8000); // Smaller samples for real draws
      
      await apiService.collectRandomnessSample(sampleSize);

      // Wait for randomness data collection
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Get quality report
      const report = await apiService.getRandomnessQualityReport(1);
      setCurrentReport(report);

      // Add to test history
      const newTest: TestHistory = {
        id: `test_${Date.now()}`,
        timestamp: new Date().toISOString(),
        testType: 'real',
        entryCount,
        sampleSize: report.sample_size || 0,
        passRate: report.pass_rate || 0,
        testsPassed: report.tests_passed || 0,
        testsRun: report.tests_run || 0,
        recommendation: report.recommendation || 'No recommendation',
        results: report.results || []
      };

      setTestHistory(prev => [newTest, ...prev]);

    } catch (err) {
      setError(`Test failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsRunning(false);
    }
  };

  const clearHistory = () => {
    setTestHistory([]);
    localStorage.removeItem('randomness-test-history');
  };

  const formatPassRate = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  const getPassRateColor = (rate: number) => {
    if (rate >= 0.9) return '#28a745';
    if (rate >= 0.8) return '#ffc107';
    return '#dc3545';
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  return (
    <div className="randomness-test">
      <div className="test-header">
        <h2>Randomness Testing Lab</h2>
        <p>Test the randomness quality of the number generator with various sample sizes</p>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="close-btn">×</button>
        </div>
      )}

      {/* Test Controls */}
      <div className="test-controls">
        <h3>Run Tests</h3>
        <div className="test-buttons">
          <div className="test-group">
            <h4>Dummy Data Tests</h4>
            <p>Large sample tests for comprehensive randomness validation</p>
            <div className="button-group">
              <button 
                onClick={() => runDummyTest(1000)} 
                disabled={isRunning}
                className="test-btn dummy"
              >
                Test 1,000 Entries
              </button>
              <button 
                onClick={() => runDummyTest(5000)} 
                disabled={isRunning}
                className="test-btn dummy"
              >
                Test 5,000 Entries
              </button>
              <button 
                onClick={() => runDummyTest(10000)} 
                disabled={isRunning}
                className="test-btn dummy"
              >
                Test 10,000 Entries
              </button>
            </div>
          </div>

          <div className="test-group">
            <h4>Real Draw Tests</h4>
            <p>Smaller sample tests simulating actual Plexus Random Draws</p>
            <div className="button-group">
              <button 
                onClick={() => runRealDrawTest(100)} 
                disabled={isRunning}
                className="test-btn real"
              >
                Test 100 Entries
              </button>
              <button 
                onClick={() => runRealDrawTest(500)} 
                disabled={isRunning}
                className="test-btn real"
              >
                Test 500 Entries
              </button>
              <button 
                onClick={() => runRealDrawTest(1000)} 
                disabled={isRunning}
                className="test-btn real"
              >
                Test 1,000 Entries
              </button>
            </div>
          </div>
        </div>

        {isRunning && (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Running randomness tests...</p>
          </div>
        )}
      </div>

      {/* Current Results */}
      {currentReport && (
        <div className="current-results">
          <h3>Latest Test Results</h3>
          <div className="results-summary">
            <div className="summary-item">
              <span className="label">Sample Size:</span>
              <span className="value">{currentReport.sample_size.toLocaleString()} bits</span>
            </div>
            <div className="summary-item">
              <span className="label">Tests Run:</span>
              <span className="value">{currentReport.tests_run}</span>
            </div>
            <div className="summary-item">
              <span className="label">Tests Passed:</span>
              <span className="value">{currentReport.tests_passed}</span>
            </div>
            <div className="summary-item">
              <span className="label">Pass Rate:</span>
              <span 
                className="value pass-rate"
                style={{ color: getPassRateColor(currentReport.pass_rate) }}
              >
                {formatPassRate(currentReport.pass_rate)}
              </span>
            </div>
          </div>
          <div className="recommendation">
            <strong>Recommendation:</strong> {currentReport.recommendation}
          </div>
        </div>
      )}

      {/* Test History */}
      {testHistory.length > 0 && (
        <div className="test-history">
          <div className="history-header">
            <h3>Test History</h3>
            <button onClick={clearHistory} className="clear-btn">
              Clear History
            </button>
          </div>
          <div className="history-table">
            <div className="table-header">
              <div>Timestamp</div>
              <div>Type</div>
              <div>Entries</div>
              <div>Sample Size</div>
              <div>Pass Rate</div>
              <div>Tests</div>
              <div>Status</div>
            </div>
            {testHistory.map((test) => (
              <div key={test.id} className="table-row">
                <div className="timestamp">{formatTimestamp(test.timestamp)}</div>
                <div className={`test-type ${test.testType}`}>
                  {test.testType === 'dummy' ? 'Dummy' : 'Real'}
                </div>
                <div className="entry-count">{test.entryCount.toLocaleString()}</div>
                <div className="sample-size">{test.sampleSize.toLocaleString()}</div>
                <div 
                  className="pass-rate"
                  style={{ color: getPassRateColor(test.passRate) }}
                >
                  {formatPassRate(test.passRate)}
                </div>
                <div className="test-count">{test.testsPassed}/{test.testsRun}</div>
                <div className={`status ${test.passRate >= 0.8 ? 'good' : 'poor'}`}>
                  {test.passRate >= 0.8 ? 'Good' : 'Poor'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {testHistory.length === 0 && !isRunning && (
        <div className="no-history">
          <p>No test history yet. Run some tests to see results here!</p>
        </div>
      )}
    </div>
  );
};

export default RandomnessTest;

